// useFirestoreDoc — single-document live listener.
// -----------------------------------------------------------------
// Per docs/architecture/m3-spa-scaffold-plan.md § 2.7 + dispatch
// m3-2-auth-rules-listener.md § 6.9 + § 3.7.
//
// Subscribes to a single Firestore document via `onSnapshot` and
// keeps a {data, loading, error} tuple in component state. The
// effect's cleanup function MUST call the returned `unsubscribe`
// to release the listener — missing this leaks both the websocket
// pair AND the React state setters past unmount, which `StrictMode`
// surfaces as console warnings and which tests will eventually
// catch via M-Test.2.
//
// `path` is a slash-delimited Firestore document path
// (`collection/doc`, `collection/doc/sub/doc`, etc.). Empty / null
// paths are not supported here — pass `null` only to skip
// subscription entirely (handy when the doc id is awaited from
// elsewhere). The hook intentionally forces a string in the type so
// callers can't accidentally pass `undefined`.

import { doc, onSnapshot, type DocumentData } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { db } from '@/lib/firebase';

export type FirestoreDocState<T> = {
  data: T | undefined;
  loading: boolean;
  error: Error | null;
};

/**
 * Subscribe to a single Firestore document.
 *
 * @param path  Slash-delimited document path (e.g. `tours_plans/abc`).
 *              Pass `null` to skip subscription (returns
 *              `{ data: undefined, loading: false, error: null }`).
 *
 * The subscription tears down on unmount AND when `path` changes;
 * the new path immediately resets loading=true so consumers can
 * render a spinner without flashing stale data.
 */
export function useFirestoreDoc<T = DocumentData>(
  path: string | null,
): FirestoreDocState<T> {
  const [state, setState] = useState<FirestoreDocState<T>>({
    data: undefined,
    loading: path !== null,
    error: null,
  });

  useEffect(() => {
    if (path === null) {
      setState({ data: undefined, loading: false, error: null });
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));
    const unsubscribe = onSnapshot(
      doc(db, path),
      (snap) => {
        setState({
          data: snap.exists() ? (snap.data() as T) : undefined,
          loading: false,
          error: null,
        });
      },
      (err) => {
        setState({ data: undefined, loading: false, error: err });
      },
    );
    return () => unsubscribe();
  }, [path]);

  return state;
}
